import type { ComponentType } from "react";
import type { RuntimeMode } from "@/runtime/types";

export type DemoId = "see" | "explore" | "engage";
export type QualityTier = "low" | "medium" | "high";

export type WatchVariant = "surgical-white" | "midnight-gold" | "commerce-green" | "khronos-red";
export type WatchInspectionMode = "assembled" | "technical";
export type WatchHotspotId = "none" | "band" | "bezel" | "buttons" | "glass" | "dial";
export type HologramStyle = "bursts" | "rainbow" | "swirl";

export interface DemoControlState {
  /** Wireframe 5: "Tap the screen to reveal specs". */
  seeFocus: "none" | "laptop" | "phone";
  /** Wireframe 6: material swatches, numbered hotspots. */
  exploreVariant: WatchVariant;
  exploreInspection: WatchInspectionMode;
  exploreHotspot: WatchHotspotId;
  /** Wireframe 7 -> 8: "Tap to flip" front/back. */
  engageSide: "front" | "back";
  engageHoloStyle: HologramStyle;
}

export type DemoAction =
  | { type: "see-focus"; value: DemoControlState["seeFocus"] }
  | { type: "explore-variant"; value: WatchVariant }
  | { type: "explore-inspection"; value: WatchInspectionMode }
  | { type: "explore-hotspot"; value: WatchHotspotId }
  | { type: "explore-reset" }
  | { type: "engage-side"; value: DemoControlState["engageSide"] }
  | { type: "engage-holo-style"; value: HologramStyle }
  | { type: "demo-reset"; demo: DemoId };

export interface DemoRuntimeSnapshot {
  mode?: RuntimeMode;
  placed?: boolean;
  placement?: { position: [number, number, number]; scale?: number };
}

export interface DemoSceneProps {
  runtime?: DemoRuntimeSnapshot;
  quality?: QualityTier;
  reducedMotion?: boolean;
  resetEpoch?: number;
  controls?: DemoControlState;
  onAction?: (action: DemoAction) => void;
  setPlacementGesturesEnabled?: (enabled: boolean) => void;
  resetPlacementRotation?: () => void;
}

/**
 * How the non-AR camera should sit for a given subject. In AR the session owns
 * the camera, but in the 3D and camera-viewer modes nothing does — and a single
 * shared pose flatters none of the three subjects. A tabletop wants to be seen
 * from above; a card wants to be met head-on and close.
 */
export interface ViewerFraming {
  position: [number, number, number];
  target: [number, number, number];
}

export interface DemoDefinition {
  id: DemoId;
  order: number;
  title: string;
  /** Wireframe caption under the chapter title. */
  hint: string;
  /** Lifts the model off the detected floor plane in AR. */
  arFloorOffsetY: number;
  viewerFraming: ViewerFraming;
  preload: () => Promise<void>;
  Scene: ComponentType<DemoSceneProps>;
}

export const initialDemoControls: DemoControlState = {
  seeFocus: "none",
  exploreVariant: "midnight-gold",
  exploreInspection: "assembled",
  exploreHotspot: "none",
  engageSide: "front",
  engageHoloStyle: "swirl",
};

export function demoControlReducer(state: DemoControlState, action: DemoAction): DemoControlState {
  switch (action.type) {
    case "see-focus":
      return { ...state, seeFocus: action.value };
    case "explore-variant":
      return { ...state, exploreVariant: action.value };
    case "explore-inspection":
      return { ...state, exploreInspection: action.value };
    case "explore-hotspot":
      return { ...state, exploreHotspot: action.value };
    case "explore-reset":
      return {
        ...state,
        exploreVariant: initialDemoControls.exploreVariant,
        exploreInspection: initialDemoControls.exploreInspection,
        exploreHotspot: initialDemoControls.exploreHotspot,
      };
    case "engage-side":
      return { ...state, engageSide: action.value };
    case "engage-holo-style":
      return { ...state, engageHoloStyle: action.value };
    case "demo-reset":
      // Wireframe "Reset" is per-demo: it must not discard the other demos'
      // state, because Next/Back returns the visitor to them.
      if (action.demo === "see") return { ...state, seeFocus: initialDemoControls.seeFocus };
      if (action.demo === "explore") return demoControlReducer(state, { type: "explore-reset" });
      return {
        ...state,
        engageSide: initialDemoControls.engageSide,
        engageHoloStyle: initialDemoControls.engageHoloStyle,
      };
    default:
      return state;
  }
}
